import {
  SOURCE_CLIENT_LABELS,
} from "../colors";
import { extractSourceItemsFromCitations } from "../../ai/sourceItems";
import type { SourceItem } from "../../ai/sourceItems";
import type { Citation } from "../../types";
import {
  applyDialogPopupWindowGeometry,
  buildCenteredDialogPopupWindow,
  focusDialogPopupWindow,
} from "../../../services/dialogPopupWindow";
import { resolveCitationDocumentUrl } from "../../../services/gcpFileUrl";
import { getSafeCitationHref } from "../renderers/VisitSourceButton";

/**
 * Cross-window transport for the citation dialog. The dialog runs in a
 * separate `window.open(...)` popup, so we use the same triple-channel
 * pattern as `deckCreatorDialogService.ts`:
 *
 *   - `localStorage` (`__finster_citation_dialog_data__`) for the
 *     payload the popup needs at first paint. The popup deletes the
 *     key on read so a stale entry can't poison the next open.
 *   - `postMessage` (kind `finster.citationDialog.result`) for the
 *     popup → parent close signal. The parent verifies `event.source`
 *     against the active popup and `event.origin` against
 *     `window.location.origin`.
 *   - `localStorage` (`__finster_citation_dialog_result__`) as a
 *     fallback for the same close signal, in case `postMessage` is
 *     dropped (some hosts drop the event when the popup is closing).
 *     Parent listens via the `storage` event.
 *   - A 500ms `popup.closed` poll as the ultimate "user dismissed it
 *     via the OS chrome" detector.
 *
 * Keep the constants below byte-identical to `CitationDialogEntry.tsx` —
 * they're the wire format. The popup is intentionally simple (no
 * config submit), so the result envelope only carries `{ closed: true }`.
 */
const DIALOG_PAYLOAD_KEY = "__finster_citation_dialog_data__";
const DIALOG_RESULT_STORAGE_KEY = "__finster_citation_dialog_result__";
const DIALOG_POST_MESSAGE_KIND = "finster.citationDialog.result";
const DIALOG_COMMAND_MESSAGE_KIND = "finster.citationDialog.command";

const CLOSE_WATCHER_INTERVAL_MS = 500;

export interface OpenCitationDialogOptions {
  /** Inline `<u id="…">` uuid clicked in chat / drawer card. */
  citationId?: string | null;
  /**
   * Fully-resolved source item (used when the user clicked
   * "View source" on a Retrieved-sources / Citations card). When set,
   * `citationId` is ignored by the popup body.
   */
  sourceItem?: SourceItem | null;
  /** Citation pool for the active assistant turn. */
  citations: Citation[];
  /**
   * Preserve PowerPoint's existing iframe capability by default. Excel sets
   * this to false so embedded source pages remain in an opaque origin.
   */
  allowIframeSameOrigin?: boolean;
}

export interface OpenCitationDialogResult {
  /** True when the popup launched; false when the popup was blocked. */
  launched: boolean;
  /** Set when `launched` is false; gives the caller a reason to log /
   *  fall back. Empty string when `launched` is true. */
  reason?: string;
  /** Safe direct-source fallback when the internal popup was blocked. */
  directHref?: string;
}

interface PopupResultEnvelope {
  kind: typeof DIALOG_POST_MESSAGE_KIND;
  payload: { closed: true };
}

type DialogCommandPayload = { type: "close" };

interface DialogCommandEnvelope {
  kind: typeof DIALOG_COMMAND_MESSAGE_KIND;
  payload: DialogCommandPayload;
}

let activePopupWindow: Window | null = null;
let activeTeardown: (() => void) | null = null;

function buildDialogUrl(): string {
  // Served from the same origin as the taskpane (`/dialogs/citation.html`)
  // so the manifest's existing `AppDomains` entry covers it — no manifest
  // change needed. See `rspack.config.js` `HtmlRspackPlugin` for the
  // emit path.
  return `${window.location.origin}/dialogs/citation.html`;
}

function isPopupResultEnvelope(value: unknown): value is PopupResultEnvelope {
  return (
    !!value &&
    typeof value === "object" &&
    (value as { kind?: unknown }).kind === DIALOG_POST_MESSAGE_KIND
  );
}

/**
 * Resolve `citationId` → `SourceItem` in the parent process so the popup
 * receives only the one envelope it needs to render, not the full
 * 56-citation deck-wide pool. The pool serialized to JSON is routinely
 * 1+ MB (FactSet envelopes embed SEC iframe HTML / markdown blobs) and
 * blows out the `localStorage.setItem` 5 MB origin quota in a single
 * write — observed `QuotaExceededError` in production.
 *
 * Back-resolution mirrors `CitationContent`'s `useMemo`: find the parent
 * envelope, extract the matching source item, prefer Markdown (one item
 * per envelope) over Dictionary (one item per link) when the uuid lives
 * in both. Returns `null` when the uuid isn't in the pool — the popup
 * then renders its empty state, which is the right UX (we know the user
 * clicked something the pool doesn't know about).
 */
function resolveSourceItemFromCitationId(
  citationId: string,
  citations: Citation[]
): SourceItem | null {
  const candidates = extractSourceItemsFromCitations(citations, {
    clientLabels: SOURCE_CLIENT_LABELS,
  });
  if (candidates.length === 0) return null;
  // Prefer items that explicitly list this uuid (Markdown envelopes
  // always do via FQL key extraction; Dictionary links list uuids
  // they "own" via `data-highlight-ids` / inline `<u id>` markers).
  const owned = candidates.find((item) => item.citedUuids.includes(citationId));
  if (owned) return owned;
  // Fall back to the first item from the envelope that holds this
  // uuid. Markdown items match any uuid in their table; Dictionary
  // items match by id substring.
  const fuzzy = candidates.find((item) => {
    if (item.kind === "table") return true;
    return item.id.includes(citationId);
  });
  return fuzzy ?? null;
}

function stagePayloadForPopup(options: OpenCitationDialogOptions): SourceItem | null {
  // Resolve `citationId` → `SourceItem` in the parent so the popup
  // receives only the one envelope it needs, not the full deck-wide
  // pool (which can easily exceed the 5 MB `localStorage.setItem`
  // single-key quota — observed `QuotaExceededError` with a 1 MB
  // payload in production). When the caller already supplied a
  // `sourceItem` (View-source card click), we just forward it.
  const sourceItem =
    options.sourceItem ??
    (options.citationId
      ? resolveSourceItemFromCitationId(options.citationId, options.citations ?? [])
      : null);
  const payload = {
    citationId: options.citationId ?? null,
    sourceItem,
    // Intentionally empty: `CitationContent` uses `props.citations`
    // only to back-resolve `citationId` → envelope, which we've
    // already done. Sending the pool again would blow the quota.
    citations: [] as Citation[],
    allowIframeSameOrigin: options.allowIframeSameOrigin !== false,
  };
  try {
    // Remove an older payload first. If the new write exceeds quota, the
    // popup must fall back to the fresh opener payload, never stale storage.
    localStorage.removeItem(DIALOG_PAYLOAD_KEY);
    localStorage.setItem(DIALOG_PAYLOAD_KEY, JSON.stringify(payload));
  } catch {
    try {
      localStorage.removeItem(DIALOG_PAYLOAD_KEY);
    } catch {
      // Non-critical: the opener fallback below remains authoritative.
    }
  }
  // Also stash on the parent window so the popup can pull it via
  // `window.opener.__finsterCitationDialogPayload__` if localStorage
  // isn't shared across the popup boundary (some host WebViews
  // partition storage per window).
  try {
    (window as Window & { __finsterCitationDialogPayload__?: unknown }).__finsterCitationDialogPayload__ =
      payload;
  } catch {
    // Non-critical fallback channel.
  }
  return sourceItem;
}

function clearStagedPayload(): void {
  try {
    localStorage.removeItem(DIALOG_PAYLOAD_KEY);
  } catch {
    // Non-critical.
  }
  try {
    delete (window as Window & {
      __finsterCitationDialogPayload__?: unknown;
    }).__finsterCitationDialogPayload__;
  } catch {
    // Non-critical.
  }
}

function clearStagedResult(): void {
  try {
    localStorage.removeItem(DIALOG_RESULT_STORAGE_KEY);
  } catch {
    // Non-critical.
  }
}

function sendCommandToPopup(popup: Window, command: DialogCommandPayload): void {
  try {
    const envelope: DialogCommandEnvelope = {
      kind: DIALOG_COMMAND_MESSAGE_KIND,
      payload: command,
    };
    popup.postMessage(envelope, window.location.origin);
  } catch {
    // ignore — popup may be closing
  }
}

function teardownExistingPopup(): void {
  if (activeTeardown) {
    try {
      activeTeardown();
    } catch {
      // ignore
    }
    activeTeardown = null;
  }
  if (activePopupWindow) {
    try {
      if (!activePopupWindow.closed) {
        sendCommandToPopup(activePopupWindow, { type: "close" });
        activePopupWindow.close();
      }
    } catch {
      // ignore
    }
    activePopupWindow = null;
  }
}

/**
 * Open the citation source-information dialog. The promise resolves when
 * the popup closes (user clicks X, hits Escape, or closes the window).
 * Currently the result is always `{ closed: true }` — the dialog has no
 * "submit" path; it's a viewer.
 */
export function openCitationDialog(
  options: OpenCitationDialogOptions
): Promise<OpenCitationDialogResult> {
  teardownExistingPopup();
  const stagedSourceItem = stagePayloadForPopup(options);
  clearStagedResult();

  return new Promise<OpenCitationDialogResult>((resolve) => {
    const dialogUrl = buildDialogUrl();
    const popupWindow = buildCenteredDialogPopupWindow();
    // Do not pass `noopener` — the popup uses `window.opener.postMessage`
    // to signal close back to the parent (matches deck-creator). The
    // popup also reads the staged payload from same-origin localStorage,
    // which works whether `noopener` is set or not, but the postMessage
    // channel needs the opener handle.
    const popup = window.open(
      dialogUrl,
      "finster_citation_dialog",
      popupWindow.features
    );

    if (!popup) {
      const directHref =
        stagedSourceItem?.kind === "document"
          ? getSafeCitationHref(
              resolveCitationDocumentUrl(
                stagedSourceItem.link.link || stagedSourceItem.link.originalPdfUrl,
              ),
            )
          : "";
      clearStagedPayload();
      resolve({
        launched: false,
        reason: "popup blocked — allow popups for this origin and retry",
        ...(directHref ? { directHref } : {}),
      });
      return;
    }

    activePopupWindow = popup;
    applyDialogPopupWindowGeometry(popup, popupWindow);
    focusDialogPopupWindow(popup);
    let settled = false;

    const teardown = (): void => {
      window.removeEventListener("message", onMessage);
      window.removeEventListener("storage", onStorage);
      window.clearInterval(closeWatcher);
      clearStagedResult();
      clearStagedPayload();
      if (activePopupWindow === popup) {
        activePopupWindow = null;
      }
      try {
        if (!popup.closed) {
          sendCommandToPopup(popup, { type: "close" });
          popup.close();
        }
      } catch {
        // popup may already be closed
      }
    };
    const settle = (): void => {
      if (settled) return;
      settled = true;
      teardown();
      activeTeardown = null;
      resolve({ launched: true });
    };
    // Programmatic close and popup replacement must settle the same promise
    // as user-driven close. Storing `settle` prevents abandoned callers.
    activeTeardown = settle;

    function onMessage(event: MessageEvent): void {
      if (event.source !== popup) return;
      if (event.origin !== window.location.origin) return;
      if (!isPopupResultEnvelope(event.data)) return;
      settle();
    }

    // Fallback channel: when `postMessage` is dropped (some hosts drop
    // it during the popup's `beforeunload`), the popup's storage write
    // fires a `storage` event on the parent window.
    function onStorage(event: StorageEvent): void {
      if (event.key !== DIALOG_RESULT_STORAGE_KEY) return;
      if (!event.newValue) return;
      settle();
    }

    window.addEventListener("message", onMessage);
    window.addEventListener("storage", onStorage);

    // Ultimate fallback: user closes the popup via the OS chrome and
    // neither postMessage nor the storage event fires before the popup
    // tears down. Poll `popup.closed` every 500ms; once closed, drain
    // any staged result and settle.
    const closeWatcher = window.setInterval(() => {
      if (!popup.closed) return;
      settle();
    }, CLOSE_WATCHER_INTERVAL_MS);
  });
}

/**
 * Imperatively close the currently-open citation dialog (if any). Used
 * when the parent decides to dismiss the dialog programmatically — for
 * example when the user navigates to a different assistant turn whose
 * citation pool no longer contains the open `citationId`.
 *
 * Safe to call when no dialog is open (no-op).
 */
export function closeCitationDialog(): void {
  teardownExistingPopup();
}
